import {
  OrgBranchGroupLeadId,
  OrgBranchLeadId
} from '@features/lead-management/types/request';
import {
  AtendeeDetailsPayload,
  EditEventProps,
  EventStatusChangeMutationProps,
  EventTicketStatusChangePayload,
  OrgAndBranchId,
  OrgBranchEventIds,
  OrgBranchGroupIds,
  OrgBranchGroupMemberIds,
  OrgBranchMemberIds,
  OrgBranchOrderIds,
  UpdateGroupStatusParams
} from '@types';

export const API_URL = process.env.REACT_APP_API_URL;

export const END_POINTS = {
  APP_CONFIG: 'v1/config',
  AUTH: {
    GENERATE_OTP: 'v1/auth/generate-otp',
    VALIDATE_OTP: 'v1/auth/validate-otp',
    REFRESH: 'v1/auth/refresh-token',
    LOGOUT: 'v1/user',
    ACCEPT_TERMS: (id: string) => `v1/user/${id}/accept-agreement`,
    GET_USER_DETAILS: (userId: string) => `v1/user/${userId}`,
    VERIFY_UPI: (userId: string) => `v1/user/${userId}/verify-upi`,
    VERIFY_BANK_DETAILS: (userId: string) =>
      `v1/user/${userId}/verify-bank-account`,
    ONBOARD_USER: (userId: string) => `v1/user/${userId}/onboard`,
    GET_ALL_USER_INVITATIONS: 'v1/invitations',
    VERIFY_PAN: (orgId: string) => `v1/organisation/${orgId}/verify-pan`,
    GET_ROLES_AND_PERMISSIONS: (orgId: string) =>
      `v1/organisation/${orgId}/role_permissions`,
    ONBOARD_ORGANIZATION: (orgId: string) => `v1/organisation/${orgId}/onboard`,
    UPDATE_INVITATION: (inviteId: string) => `v1/invitations/${inviteId}`,
    VERIFY_GSTIN: (orgId: string) => `v1/organisation/${orgId}/verify-gstin`,
    UPGRADE_LITE: (orgId: string) => `v1/organisation/${orgId}/kyc/submit`,
    GET_ALL_PLANS: (orgId: string) => `v1/organisation/${orgId}/plans`,
    ADD_SUBSCRIPTION: (orgId: string) =>
      `v1/organisation/${orgId}/subscription`,
    REFERRAL_CODE: (orgId: string) =>
      `v1/organisation/${orgId}/process-referral-code`,
    REFERRAL_STATUS: (orgId: string) =>
      `v1/organisation/${orgId}/referral-join-status`,
    UPDATE_WELCOME_TOUR: (userId: string) =>
      `v1/user/${userId}/complete-welcome-tour`,
    UPDATE_BANK_ACCOUNT: (organizationId: string, bankId: number) =>
      `v1/organisation/${organizationId}/bank-account/${bankId}`,
    GET_KYC_DETAILS: (orgId: string) => `v1/organisation/${orgId}/kyc-details`
  },
  GROUP_MANAGEMENT: {
    GET_ALL_GROUPS_WO_PAGINATION: ({
      organizationId,
      branchId
    }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/groups`,
    GET_ALL_GROUPS: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group`,
    GET_GROUP_MEMBERS: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/members`,
    DELETE_MEMBER: ({ organizationId, branchId, groupId }: OrgBranchGroupIds) =>
      `v3/organisation/${organizationId}/branch/${branchId}/group/${groupId}`,
    UPDATE_MEMBER: ({
      organizationId,
      branchId,
      groupId,
      memberId
    }: OrgBranchGroupMemberIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/member/${memberId}`,
    UPDATE_MEMBER_STATUS: ({
      organizationId,
      branchId,
      groupId,
      memberId
    }: OrgBranchGroupMemberIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/member/${memberId}/status`,
    GET_GROUP_BY_ID: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}`,
    UPDATE_GROUP_STATUS: ({
      organizationId,
      branchId,
      groupId,
      status
    }: UpdateGroupStatusParams) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/${status}`,
    CREATE_GROUP: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v4/organisation/${organizationId}/branch/${branchId}/group`,
    UPDATE_GROUP: ({ organizationId, branchId, groupId }: OrgBranchGroupIds) =>
      `v4/organisation/${organizationId}/branch/${branchId}/group/${groupId}`,
    DOWNLOAD_REPORT: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/report`,
    CREATE_MEMBER_IN_EXISTING_GROUP: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v2/organisation/${organizationId}/branch/${branchId}/group/${groupId}`,
    ADD_MEMBER_TO_GROUP_V3: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v3/organisation/${organizationId}/branch/${branchId}/group/${groupId}`,
    GET_ORGANIZATION_CONFIG: (organizationId: string) =>
      `v1/organisation/${organizationId}/config`,
    GET_ORGANIZATION_FEATURE_FLAGS: (organizationId: string) =>
      `v1/organisation/${organizationId}/feature`,
    GET_MEMBER_BY_ID: ({
      organizationId,
      branchId,
      groupId,
      memberId
    }: OrgBranchGroupMemberIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/member/${memberId}`,
    GET_GROUP_MEMBER_DETAILS: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v2/organisation/${organizationId}/branch/${branchId}/group/${groupId}/members/contact_numbers`,
    GET_GROUP_TRANSACTIONS: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order`,
    SEND_GROUP_REMINDER: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/payment-order/remind-all`,
    CHECK_MEMBER_PAYMENT_LINK_SENT: ({
      organizationId,
      branchId,
      groupId,
      memberId
    }: OrgBranchGroupMemberIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/member/${memberId}/check-payment-link-sent`,
    CHECK_GROUP_PAYMENT_LINK_SENT: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v4/organisation/${organizationId}/branch/${branchId}/group/${groupId}/check-payment-link-sent`,
    INTRO_MESSAGE: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/intro-message`
  },
  UPLOAD: {
    IMAGEUPLOAD: 'v1/image/upload',
    DELETE_IMAGE: 'v1/image/delete'
  },
  BRANCH_MANAGEMENT: {
    GET_ALL_BRANCHES: (organizationId: string) =>
      `v1/organisation/${organizationId}/branch`,
    UPDATE_BRANCH: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}`,
    CREATE_BRANCH: (organizationId: string) =>
      `v1/organisation/${organizationId}/branch`,
    ONBOARD_BRANCH: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/onboard`,
    GET_BRANCH_BY_ID: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}`,
    GET_PRESIGNED_URL: (organizationId: string, fileName: string) =>
      `files/presigned-url/bank_document/${organizationId}/${fileName}`,
    LINK_BANK_DOCUMENT: (organizationId: string) =>
      `v1/organisation/${organizationId}/bank-document`,
    GET_BANK_DETAILS: (organizationId: string, bankId: string) =>
      `v1/organisation/${organizationId}/account-detail/${bankId}`,
    DOWNLOAD_BANK_DOCUMENT: (fileId: string, accessKey: string) =>
      `_files/download/${fileId}/${accessKey}`,
    DELETE_BANK_DOCUMENT: (organizationId: string) =>
      `v1/organisation/${organizationId}/bank-document`
  },

  TEAM_MANAGEMENT: {
    INVITE_MEMBER: (organizationId: string) =>
      `v1/organisation/${organizationId}/invitations`,
    GET_ROLE_COUNT: (organizationId: string) =>
      `v1/organisation/${organizationId}/user-roles/count`,
    GET_ALL_ORGANIZATION_INVITED_MEMBERS: (organizationId: string) =>
      `v1/organisation/${organizationId}/invited_members`,
    GET_ALL_ORGANIZATION_MEMBERS: (organizationId: string) =>
      `v1/organisation/${organizationId}/members`,
    DELETE_ORGANIZATION_MEMBER: (
      organizationId: string,
      user_org_role_Id: string
    ) => `v1/organisation/${organizationId}/user-role/${user_org_role_Id}`,
    EDIT_ROLE: (organizationId: string, user_org_role_Id: string) =>
      `v1/organisation/${organizationId}/user-role/${user_org_role_Id}`,
    GET_USER_ROLE_DETAILS: (organizationId: string, user_org_role_Id: number) =>
      `v1/organisation/${organizationId}/user-role/${user_org_role_Id}`,
    DELETE_UNACCEPTED_INVITATION: (
      organizationId: string,
      invitationId: string
    ) => `v1/organisation/${organizationId}/invitations/${invitationId}`
  },
  ORGANIZATION: {
    GET_ORGANIZATION_LIST: 'v1/organisation',
    GET_ORGANIZATION_ACCOUNT_DETAILS: (organizationId: string) =>
      `v1/organisation/${organizationId}/account-detail`,
    GET_ORGANIZATION_ACCOUNT_DETAILS_WITH_PAGINATION: (
      organizationId: string
    ) => `v2/organisation/${organizationId}/account-detail`,
    CREATE_ORGANIZATION: 'v1/organisation',
    GET_REUPLOAD_LIST: (organizationId: string) =>
      `v1/organisation/${organizationId}/documents`,
    UPDATE_ORGANIZATION_BY_ID: (organizationId: string) =>
      `v1/organisation/${organizationId}`,
    UPDATE_ORG_CONFIG: (organizationId: string) =>
      `v1/organisation/${organizationId}/config`,
    GET_ALL_WEBHOOKS: 'v1/integrations/trigger/list',
    GET_ALL_SUPPORTED_EVENTS: 'v1/integrations/trigger/supported-events',
    CREATE_WEBHOOK: 'v1/integrations/trigger',
    GET_WEBHOOK_BY_ID: (webhookId: string) =>
      `v1/integrations/trigger/${webhookId}`,
    UPDATE_WEBHOOK: (webhookId: string) =>
      `v1/integrations/trigger/${webhookId}`,
    DELETE_WEBHOOK: (webhookId: string) =>
      `v1/integrations/trigger/${webhookId}`,
    GET_ALL_API_KEYS: 'v1/integrations/org-app/list',
    UPDATE_API_KEY: (apiId: string) => `v1/integrations/org-app/${apiId}`,
    CREATE_API_KEY: 'v1/integrations/org-app',
    DELETE_API_KEY: (apiId: string) => `v1/integrations/org-app/${apiId}`,
    GET_PROVIDERS: 'v1/notification-provider/supported-providers',
    GET_SAMPLE_TEMPLATES: '/v1/notification-provider/supported-templates',
    UPDATE_PROVIDER_CONFIG: (organizationId: string) =>
      `/v1/organisation/${organizationId}/notification-provider/org-config`,
    GET_PROVIDER_CONFIG: (organizationId: string) =>
      `/v1/organisation/${organizationId}/notification-provider/org-config`,
    UPDATE_TEMPLATE_CONFIG: (organizationId: string) =>
      `/v1/organisation/${organizationId}/notification-template/config`,
    GET_TEMPLATE_CONFIG: (organizationId: string, providerId: string) =>
      `/v1/organisation/${organizationId}/notification-template/config/${providerId}`,
    GET_ESIGN_DOCUMENTS: (orgId: string) =>
      `v1/organisation/${orgId}/e-sign/documents`,
    INITIATE_ESIGN: (orgId: string) =>
      `v1/organisation/${orgId}/e-sign/initiate-e-sign`,
    GET_ESIGN_STATUS: (orgId: string, documentId: string) =>
      `v1/organisation/${orgId}/e-sign/${documentId}/status`,
    GET_ESIGN_DETAILS: (orgId: string, documentId: string) =>
      `v1/organisation/${orgId}/e-sign/${documentId}/details`,
    GET_ESIGN_COMPLETED_STATUS: (orgId: string, documentId: string) =>
      `v1/organisation/${orgId}/e-sign/${documentId}/signed-status`
  },
  ADMIN: {
    GET_ORGANIZATION_LIST: 'v1/admin/organisations',
    GET_ORGANIZATION_DOCUMENTS: (organizationId: string) =>
      `v1/organisation/${organizationId}/documents`,
    GET_ROLES_AND_PERMISSIONS: 'v1/admin/role_permissions',
    UPDATE_ORGANIZATION_STATUS: (organizationId: string) =>
      `v1/admin/organisation/${organizationId}/status`,
    GET_ORGANIZATION_BY_ID: (organizationId: string) =>
      `v1/admin/organisation/${organizationId}`,
    UPDATE_ORG_DOC_STATUS: (docId: string) =>
      `v1/admin/document/${docId}/status`,
    GET_ORGANIZATION_CUSTOMER_LIST: `v1/admin/organisation/customers`,
    UPDATE_ORGANIZATION_CUSTOMER_SETTINGS: (orgId: string) =>
      `v1/admin/organisation/${orgId}/details`,
    EXECUTE_COMMAND: 'v1/admin/command/execute',
    GET_BUSINESS_CATEGORIES: 'v1/admin/organisation/categories',
    GET_LATEST_SIGNED_DOCUMENT: (organizationId: string) =>
      `v1/admin/organisation/${organizationId}/latest-signed-document`,
    GET_PLANS: 'v1/admin/plans'
  },
  HELP: {
    GET_FAQ_LIST:
      'v1/app-info?include_help_section=true&include_messages_section=false',
    USER_FEEDBACK: (id: string) => `v1/user/${id}/feedback`
  },
  ATTENDANCE: {
    GET_ATTENDANCE_LIST: (
      organizationId: string,
      branchId: string,
      groupId: string
    ) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/attendance/list`,
    MARK_UNMARK_HOLIDAY: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/mark-unmark-holiday`,
    DOWNLOAD_ATTENDANCE_REPORT: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/attendance/report`,
    GET_ATTENDANCE_REPORT_JOB_ID: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v2/organisation/${organizationId}/branch/${branchId}/group/${groupId}/attendance/report`,
    GET_GROUP_MEMBER_DETAILS: ({
      organizationId,
      branchId,
      groupId,
      memberId
    }: OrgBranchGroupMemberIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/member/${memberId}`,
    UPDATE_MEMBER_ATTENDANCE: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/attendance`,
    GET_ATTENDANCE_SUMMARY: ({
      organizationId,
      branchId,
      groupId
    }: OrgBranchGroupIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/attendance/detailed-summary`
  },
  TRANSACTIONS: {
    GET_PAYMENT_LIST: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order`,
    GET_PAYMENT_ORDER_HISTORY: ({
      organizationId,
      branchId,
      orderId
    }: OrgBranchOrderIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/${orderId}`,
    MARK_AS_PAID: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v2/organisation/${organizationId}/branch/${branchId}/payment-order/mark-paid`,
    SEND_PAYMENT_REMINDER: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/remind`,
    DISABLE_PAYMENT_ORDER: ({
      organizationId,
      branchId,
      orderId
    }: OrgBranchOrderIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/${orderId}/disable`,
    DOWNLOAD_PAYMENT_RECEIPT: (orderId: string) => `p/${orderId}/download`,
    GET_PAYMENT_DETAILS_BY_ID: ({
      organizationId,
      branchId,
      orderId
    }: OrgBranchOrderIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/${orderId}/details`,
    ENABLE_PARTIAL_PAYMENT: ({
      organizationId,
      branchId,
      orderId
    }: OrgBranchOrderIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/${orderId}/split-payment/enable`,
    CLOSE_PAYMENT: ({ organizationId, branchId, orderId }: OrgBranchOrderIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/${orderId}/close`,
    APPLY_DISCOUNT: ({
      organizationId,
      branchId,
      orderId
    }: OrgBranchOrderIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/${orderId}/discount`,
    UPDATE_DUE: ({ organizationId, branchId, orderId }: OrgBranchOrderIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/${orderId}/discount/amounts`
  },
  DASHBOARD: {
    GET_BRANCH_REVENUE: (organizationId: string) =>
      `v1/organisation/${organizationId}/branch-revenue`,
    GET_GROUP_PERFOMANCE: (organizationId: string) =>
      `v1/organisation/${organizationId}/group-performance`,
    GET_PAYMENT_LINKS_AND_MODES: (organizationId: string) =>
      `v1/organisation/${organizationId}/payment-links-modes`,
    GET_PAYMENT_OVERVIEW: (orgId: string) =>
      `v1/organisation/${orgId}/payment-summary`,
    GET_PAYMENT_VOLUME: (organizationId: string) =>
      `v1/organisation/${organizationId}/daily-payment-volume`
  },
  SUBSCRIPTION: {
    GET_CURRENT_SUBSCRIPTION: (organizationId: string) =>
      `v1/organisation/${organizationId}/subscription`,
    GET_SUBSCRIPTION_PLANS: (organizationId: string) =>
      `v1/organisation/${organizationId}/plans`,
    SELECT_SUBSCRIPTION: (organizationId: string) =>
      `v1/organisation/${organizationId}/subscription`
  },
  REPORTS: {
    GST_REPORT: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/gst-report`,
    REPORTS: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/payment-order/custom-report`,
    MEMBERS_EXPORT: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/members/export`,
    CUSTOM_REPORT_JOB_ID: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v2/organisation/${organizationId}/branch/${branchId}/payment-order/custom-report`,
    GST_REPORT_JOB_ID: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v2/organisation/${organizationId}/branch/${branchId}/payment-order/gst-report`,
    GET_DOWNLOAD_FILE_STATUS: (jobId: string) => `v1/job/${jobId}`
  },
  CREATE_QUICK_COLLECT_LINK: (organizationId: string, branchId: string) =>
    `v1/organisation/${organizationId}/branch/${branchId}/payment-order/instant-link`,
  MEMBER_MANAGEMENT: {
    GET_MEMBERS: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/members`,
    GET_MEMBER_COUNT: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/members/export/count`,
    GET_MEMBER_DETAILS: ({
      organizationId,
      branchId,
      memberId
    }: OrgBranchMemberIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/member/${memberId}`,
    CREATE_MEMBER: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/member`,
    UPDATE_MEMBER: ({ organizationId, branchId }: OrgAndBranchId) =>
      `v1/organisation/${organizationId}/branch/${branchId}/member`,
    DELETE_MEMBER: ({
      organizationId,
      branchId,
      memberId
    }: OrgBranchMemberIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/member/${memberId}`
  },
  EVENT: {
    ATTENDEE_LIST: ({ organizationId, branchId, eventId }: OrgBranchEventIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/event/${eventId}/attendee`,
    ATTENDEE_DETAILS: ({
      organizationId,
      branchId,
      eventId,
      attendeeId
    }: AtendeeDetailsPayload) =>
      `v1/organisation/${organizationId}/branch/${branchId}/event/${eventId}/attendee/${attendeeId}`,
    DETAILS: ({ organizationId, branchId, eventId }: OrgBranchEventIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/event/${eventId}/overview`,
    TICKET_STATUS_CHANGE: ({
      organizationId,
      branchId,
      eventId,
      ticketId,
      status
    }: EventTicketStatusChangePayload) =>
      `v1/organisation/${organizationId}/branch/${branchId}/event/${eventId}/ticket/${ticketId}/${status}`,
    EVENT_FORM_DETAILS: ({ eventId }: { eventId: string }) =>
      `v1/event/${eventId}/form`,
    CREATE: ({ organizationId, branchId }: OrgAndBranchId) =>
      `/v1/organisation/${organizationId}/branch/${branchId}/event`,

    EDIT: ({ organizationId, branchId, eventId }: EditEventProps) =>
      `/v1/organisation/${organizationId}/branch/${branchId}/event/${eventId}`,
    LIST: ({ organizationId, branchId }: OrgAndBranchId) =>
      `/v1/organisation/${organizationId}/branch/${branchId}/event`,
    STATUS_CHANGE: ({
      organizationId,
      branchId,
      eventId,
      status
    }: EventStatusChangeMutationProps) =>
      `v1/organisation/${organizationId}/branch/${branchId}/event/${eventId}/${status}`,
    DOWNLOAD_ATTENDEE_LIST: ({
      organizationId,
      branchId,
      eventId
    }: OrgBranchEventIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/event/${eventId}/attendee/export`,
    GET_COUPONS: ({ organizationId, branchId, eventId }: OrgBranchEventIds) =>
      `v1/organisation/${organizationId}/branch/${branchId}/event/${eventId}/coupons`
  },
  EXPENSE_TRACKER: {
    CREATE_EXPENSE: (organizationId: string) =>
      `v1/organisation/${organizationId}/expense`,
    GET_EXPENSES: (organizationId: string) =>
      `v1/organisation/${organizationId}/expense/list`,
    GET_EXPENSE_BY_ID: (organizationId: string, expenseId: string) =>
      `v1/organisation/${organizationId}/expense/${expenseId}`,
    UPDATE_EXPENSE: (organizationId: string, expenseId: string) =>
      `v1/organisation/${organizationId}/expense/${expenseId}`,
    DELETE_EXPENSE: (organizationId: string, expenseId: string) =>
      `v1/organisation/${organizationId}/expense/${expenseId}`,
    APPROVE_EXPENSE: (organizationId: string, expenseId: string) =>
      `v1/organisation/${organizationId}/expense/${expenseId}/approve`,
    REJECT_EXPENSE: (organizationId: string, expenseId: string) =>
      `v1/organisation/${organizationId}/expense/${expenseId}/reject`,
    GET_ALL_EXPENSE_CATEGORIES: (organizationId: string) =>
      `v1/organisation/${organizationId}/expense/category/all`,
    SEARCH_CATEGORIES: (organizationId: string) =>
      `v1/organisation/${organizationId}/expense/search`,
    GET_EXPENSE_STATS: (organizationId: string) =>
      `v1/organisation/${organizationId}/expense/tracker/stats`,
    GET_PRESIGNED_URL: (branchId: string, fileName: string) =>
      `files/presigned-url/expense-manager/${branchId}/${fileName}`,
    DOWNLOAD_FILE: (fileId: string, accessKey: string, signature: string) =>
      `_files/download/${fileId}/${accessKey}?signature=${signature}`
  },
  LEAD_MANAGEMENT: {
    GET_PRIVATE_FILE_URL: (documentId: string) => `v1/doc-url/${documentId}`,
    GET_LEADS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/lead`,
    GET_ADS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/lead-ingestion/ad`,
    CREATE_LEAD: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/lead`,
    GET_LEAD_DETAILS: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}`,
    UPDATE_LEAD: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}`,
    DELETE_LEAD: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}`,
    DOWNLOAD_CSV_TEMPLATE: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/lead/csv-import-template`,
    GET_AGENTS: (organizationId: string) =>
      `v1/organisation/${organizationId}/sales-agents`,
    GET_CATEGORIES: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/category`,
    CREATE_CATEGORY: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/category`,
    UPDATE_CATEGORY: (organizationId: string, categoryId: string) =>
      `v1/lms/organization/${organizationId}/category/${categoryId}`,
    DELETE_CATEGORY: (organizationId: string, categoryId: string) =>
      `v1/lms/organization/${organizationId}/category/${categoryId}`,
    UPDATE_STATUS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/status`,
    GET_STATUS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/status`,
    GET_TAGS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/tags`,
    CREATE_TAG: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/tags`,
    UPDATE_TAG: (organizationId: string, tagId: string) =>
      `v1/lms/organization/${organizationId}/tags/${tagId}`,
    DELETE_TAG: (organizationId: string, tagId: string) =>
      `v1/lms/organization/${organizationId}/tags/${tagId}`,
    LEAD_BULK_UPDATE: `v1/lms/organization/bulk-actions`,
    GET_LEAD_TASKS: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/activity/task`,
    CREATE_LEAD_TASK: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/activity/task`,
    DELETE_LEAD_TASK: (
      organizationId: string,
      leadId: string,
      taskId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/activity/task/${taskId}`,
    GET_LEAD_EVENTS: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/activity/event`,
    CREATE_LEAD_EVENT: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/activity/event`,
    DELETE_LEAD_EVENT: (
      organizationId: string,
      leadId: string,
      eventId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/activity/event/${eventId}`,
    GET_LEAD_CALLS: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/activity/call`,
    CREATE_LEAD_CALL: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/activity/call`,
    DELETE_LEAD_CALL: (
      organizationId: string,
      leadId: string,
      callId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/activity/call/${callId}`,
    GET_LEADS_DASHBOARD: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/dashboard`,
    GET_AGENTS_STATUS_SUMMARY: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/dashboard/agents`,
    UPDATE_LEAD_ASSIGNEE: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/assign`,
    GET_GOOGLE_STATUS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/google/status`,
    GET_GOOGLE_AUTH_URL: (organizationId: string, redirectUrl: string) =>
      `v1/lms/organization/${organizationId}/google/auth?redirect-url=${redirectUrl}`,
    DELETE_GOOGLE_AUTH: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/google/auth`,
    GET_GOOGLE_USER_ORG_SETTINGS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/settings/user-org-settings`,
    UPDATE_GOOGLE_USER_ORG_SETTINGS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/settings/user-org-settings`,
    GET_LEAD_ORG_CONFIG: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/config`,
    UPDATE_LEAD_ORG_CONFIG: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/config`,
    GET_LEAD_NOTES: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/notes`,
    CREATE_LEAD_NOTE: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/notes`,
    GET_LEAD_ATTACHMENTS: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/attachments`,
    DELETE_LEAD_NOTE: (
      organizationId: string,
      leadId: string,
      noteId: string
    ) => `v1/lms/organization/${organizationId}/lead/${leadId}/notes/${noteId}`,
    GET_LEAD_TIMELINE: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/timeline`,
    LEAD_BULK_CREATE: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/lead/bulk-create`,
    UPLOAD_FILE: (organizationId: string, fileType: string) =>
      `v1/lms/organization/${organizationId}/document/presigned-url?file_type=${fileType}`,
    GET_FILE_URL: (organizationId: string, documentId: string) =>
      `v1/lms/organization/${organizationId}/document/${documentId}`,
    UPDATE_LEAD_NOTE: (
      organizationId: string,
      leadId: string,
      noteId: string
    ) => `v1/lms/organization/${organizationId}/lead/${leadId}/notes/${noteId}`,
    GET_LOCATIONS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/lead/cities`,
    GENERATE_REPORT: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/report/generate`,
    GET_REPORT_JOB_STATUS: (jobId: string, organizationId: string) =>
      `v1/lms/organization/${organizationId}/report/${jobId}/status`,
    GET_COUNT_BY_STATUS_ID: (organizationId: string, statusId: string) =>
      `v1/lms/organization/${organizationId}/lead/ids/status/${statusId}`,
    BULK_UPDATE_STATUS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/lead/bulk-update-status`,
    LEAD_ADD_TO_BRANCH_GROUP_DETAILS: (organizationId: string) =>
      `v1/organisation/${organizationId}/members`,
    LEAD_ADD_TO_BRANCH: ({
      organizationId,
      leadId,
      branchId
    }: OrgBranchLeadId) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}`,
    LEAD_ADD_TO_GROUP: ({
      organizationId,
      leadId,
      branchId,
      groupId
    }: OrgBranchGroupLeadId) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/group/${groupId}`,
    LEAD_EDIT_MEMBER: (
      organizationId: string,
      leadId: string,
      branchId: string,
      groupId: string,
      memberId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/group/${groupId}/member/${memberId}`,
    LEAD_MEMBER_FEE_COLLECTION_SETTINGS: (
      organizationId: string,
      branchId: string,
      groupId: string,
      memberId: string
    ) =>
      `v1/organisation/${organizationId}/branch/${branchId}/group/${groupId}/member/${memberId}/fee-collection-settings`,
    LEAD_BRANCH_GROUP_DETAILS: (organizationId: string) =>
      `v1/organisation/${organizationId}/members`,
    LEAD_QUICK_COLLECT: (
      organizationId: string,
      leadId: string,
      branchId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/payment-order/instant-link`,
    LEAD_MARK_AS_PAID: (
      organizationId: string,
      leadId: string,
      branchId: string,
      paymentOrderId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/payment-order/${paymentOrderId}/mark-paid`,
    LEAD_SEND_PAYMENT_REMINDER: (
      organizationId: string,
      leadId: string,
      branchId: string,
      paymentOrderId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/payment-order/${paymentOrderId}/remind`,
    LEAD_DISABLE_PAYMENT_ORDER: (
      organizationId: string,
      leadId: string,
      branchId: string,
      paymentOrderId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/payment-order/${paymentOrderId}/disable`,
    LEAD_DISCOUNT: (
      organizationId: string,
      leadId: string,
      branchId: string,
      paymentOrderId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/payment-order/${paymentOrderId}/discount`,
    LEAD_DOWNLOAD_PAYMENT_RECEIPT: (
      organizationId: string,
      leadId: string,
      paymentOrderId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/p/${paymentOrderId}/download`,
    GET_LEAD_PAYMENT_ORDERS: (organizationId: string, leadId: string) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/payment-orders`,
    GET_ALL_TASKS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/activities/tasks`,
    GET_ALL_EVENTS: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/activities/events`,
    GET_META_PROVIDER: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/lead-ingestion/provider`,
    CREATE_META_PROVIDER: (organizationId: string) =>
      `v1/lms/organization/${organizationId}/lead-ingestion/provider`,
    UPDATE_META_PROVIDER: (organizationId: string, providerId: string) =>
      `v1/lms/organization/${organizationId}/lead-ingestion/provider/${providerId}`,
    GET_JOB_STATUS: (organizationId: string, jobId: string) =>
      `v1/lms/organization/${organizationId}/job/${jobId}`,
    LEAD_CLOSE_PAYMENT: (
      organizationId: string,
      leadId: string,
      branchId: string,
      paymentOrderId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/payment-order/${paymentOrderId}/close`,
    ENABLE_SPLIT_PAYMENT: (
      organizationId: string,
      leadId: string,
      branchId: string,
      paymentOrderId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/payment-order/${paymentOrderId}/split-payment/enable`,
    GET_LEAD_PAYMENT_ORDER_DETAILS: (
      organizationId: string,
      leadId: string,
      branchId: string,
      paymentOrderId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/payment-order/${paymentOrderId}/details`,
    GET_LEAD_PAYMENT_ORDER_HISTORY: (
      organizationId: string,
      leadId: string,
      branchId: string,
      paymentOrderId: string
    ) =>
      `v1/lms/organization/${organizationId}/lead/${leadId}/branch/${branchId}/payment-order/${paymentOrderId}/split-payment/history`
  },
  TALLY_INTEGRATION: {
    CREATE_TALLY_INTEGRATION: 'v1/integrations/accounting-platform-config',
    GET_TALLY_CONFIG: 'v1/integrations/accounting-platform-config/tally',
    UPDATE_TALLY_CONFIG: (id: string) =>
      `v1/integrations/accounting-platform-config/${id}`,
    UPDATE_BANK_LEDGER_NAMES: (id: string) =>
      `v1/organisation/${id}/bank-ledgers`
  }
};
